import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import {
  Truck, Loader2, Package, ChevronRight, ChevronDown, Send, X,
  AlertTriangle, Plus, FileDown, History,
} from "lucide-react";
import MediaInputRow from "@/components/shared/MediaInputRow";
import { fileUrl, uploadMany } from "@/lib/uploads";
import ProjectSearchBar, { ProjectSearchEmpty, projectMatches } from "@/components/shared/ProjectSearchBar";
import { MATERIAL_CATEGORIES } from "@/pages/factory/FactoryOrdersPage";

const TEAM_OPTIONS = [
  { key: "factory",     label: "Factory Team" },
  { key: "purchase",    label: "Purchase Team" },
  { key: "imos",        label: "IMOS Team" },
  { key: "procurement", label: "Procurement Team" },
  { key: "design",      label: "Design Team" },
];

const STATUS_TONE = {
  pending:     "border-gray-500/40 text-gray-400 bg-gray-500/5",
  received:    "border-sky-500/40 text-sky-300 bg-sky-500/5",
  in_progress: "border-[#d4af37]/40 text-[#d4af37] bg-[#d4af37]/5",
  dispatched:  "border-emerald-500/40 text-emerald-300 bg-emerald-500/5",
  rejected:    "border-red-500/40 text-red-400 bg-red-500/5",
};

export default function ComponentDispatchPage() {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [selectedId, setSelectedId] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/execution/my-projects");
        setProjects(data.projects || []);
        if ((data.projects || []).length) setSelectedId(data.projects[0].id);
      } catch (e) {
        toast.error(formatApiErrorDetail(e.response?.data?.detail));
      } finally { setLoading(false); }
    })();
  }, []);

  const filtered = useMemo(() =>
    projects.filter((p) => projectMatches(p, q)),
    [projects, q]);

  const selected = filtered.find((p) => p.id === selectedId) || projects.find((p) => p.id === selectedId);

  return (
    <div className="min-h-full">
      <div className="border-b border-[#262626] px-6 md:px-10 py-6 bg-[#050505]/80 sticky top-0 z-30">
        <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#d4af37]">Component Dispatch</p>
        <h1 className="font-display text-2xl md:text-3xl text-white mt-1">Dispatches &amp; Material Requests</h1>
      </div>

      <div className="px-6 md:px-10 py-6 grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-1 space-y-3">
          <ProjectSearchBar value={q} onChange={setQ}
                            resultCount={filtered.length} totalCount={projects.length}
                            testid="cd-projects-search"/>
          {loading ? (
            <div className="iz-card p-6 flex items-center gap-2 text-xs text-gray-500">
              <Loader2 className="w-4 h-4 animate-spin"/> Loading…
            </div>
          ) : filtered.length === 0 && q.trim() ? (
            <ProjectSearchEmpty query={q} testid="cd-search-empty"/>
          ) : filtered.length === 0 ? (
            <div className="iz-card p-6 text-center text-xs text-gray-500 italic">
              No projects assigned yet.
            </div>
          ) : (
            filtered.map((p) => (
              <button key={p.id} onClick={() => setSelectedId(p.id)}
                      className={`w-full text-left iz-card p-3 transition-colors ${
                        p.id === selectedId ? "border-[#d4af37]" : "hover:border-[#d4af37]/40"
                      }`}
                      data-testid={`cd-project-${p.id}`}>
                <p className="text-sm text-white truncate">{p.client_name || "—"}</p>
                <p className="text-[11px] text-gray-500 truncate">{p.project_name}</p>
                <p className="text-[10px] text-gray-600 mt-1">{p.city}</p>
              </button>
            ))
          )}
        </div>

        <div className="lg:col-span-2">
          {selected ? (
            <ProjectDispatchPanel key={selected.id} project={selected}/>
          ) : (
            <div className="iz-card p-8 text-center text-sm text-gray-500 italic">
              Pick a project on the left to see its dispatches.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function ProjectDispatchPanel({ project }) {
  const [dispatches, setDispatches] = useState([]);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showRequest, setShowRequest] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [d, r] = await Promise.all([
        api.get(`/factory/orders/${project.imos_order_id || project.project_id || project.id}/dispatches`).catch(() => ({ data: [] })),
        api.get(`/material-requests?project_id=${project.project_id || project.id}&scope=mine`).catch(() => ({ data: { requests: [] } })),
      ]);
      setDispatches(Array.isArray(d.data) ? d.data : (d.data.dispatches || []));
      setRequests(r.data.requests || []);
    } finally { setLoading(false); }
  }, [project]);

  useEffect(() => { load(); }, [load]);

  const matLabel = (k) => MATERIAL_CATEGORIES.find((c) => c.key === k)?.label || k;

  return (
    <div className="space-y-4">
      <div className="iz-card p-5">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <div>
            <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#d4af37]">Dispatch History</p>
            <p className="text-sm text-white mt-1">{project.client_name} · {project.project_name}</p>
          </div>
          <button onClick={() => setShowRequest(true)}
                  className="iz-btn-primary text-xs flex items-center gap-1.5"
                  data-testid="request-material-btn">
            <Plus className="w-3.5 h-3.5"/> Request Material
          </button>
        </div>
        {loading ? (
          <div className="text-xs text-gray-500 flex items-center gap-2">
            <Loader2 className="w-3.5 h-3.5 animate-spin"/> Loading…
          </div>
        ) : dispatches.length === 0 ? (
          <p className="text-xs text-gray-500 italic">No dispatches yet on this project.</p>
        ) : (
          <ul className="space-y-2" data-testid="dispatch-history-list">
            {dispatches.map((d) => (
              <li key={d.id} className="border border-[#161616] rounded px-3 py-2"
                  data-testid={`dispatch-${d.id}`}>
                <div className="flex justify-between gap-3 flex-wrap">
                  <div className="min-w-0">
                    <p className="text-sm text-white">
                      {d.material_category === "other" ? d.material_other : matLabel(d.material_category)}
                    </p>
                    <p className="text-[11px] text-gray-500 mt-0.5">
                      Driver {d.driver_name || "—"} · Vehicle <span className="font-mono">{d.vehicle_number || "—"}</span>
                    </p>
                    {d.description && (
                      <p className="text-[11px] text-gray-400 mt-1 whitespace-pre-wrap">{d.description}</p>
                    )}
                    {Array.isArray(d.bom_items) && d.bom_items.length > 0 && (
                      <ul className="mt-2 border-l border-[#262626] pl-3 space-y-0.5"
                          data-testid={`dispatch-${d.id}-bom-breakdown`}>
                        {d.bom_items.map((b, i) => (
                          <li key={`${b.bom_item_id || i}`} className="text-[10px] text-gray-400">
                            <span className="text-white">{b.item_name}</span>
                            {b.vendor && <span className="text-gray-500"> · {b.vendor}</span>}
                            <span className="font-mono text-gray-500 ml-1">
                              · {b.qty}{b.unit ? ` ${b.unit}` : ""}
                            </span>
                          </li>
                        ))}
                      </ul>
                    )}
                    {(d.attachment_file_ids || []).length > 0 && (
                      <p className="text-[10px] text-gray-500 mt-1">
                        {d.attachment_file_ids.map((fid, i) => (
                          <a key={fid} href={fileUrl(fid)} target="_blank" rel="noreferrer"
                             className="text-[#d4af37] hover:underline mr-2 inline-flex items-center gap-1">
                            <FileDown className="w-3 h-3"/> file {i + 1}
                          </a>
                        ))}
                      </p>
                    )}
                  </div>
                  <div className="text-right shrink-0">
                    <p className="text-[10px] font-mono uppercase tracking-widest text-[#d4af37]">{new Date(d.dispatched_at).toLocaleDateString()}</p>
                    <p className="text-[10px] text-gray-500">by {d.dispatched_by_name}</p>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="iz-card p-5">
        <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#d4af37] mb-3">
          Material Requests ({requests.length})
        </p>
        {requests.length === 0 ? (
          <p className="text-xs text-gray-500 italic">You haven't raised a request on this project yet.</p>
        ) : (
          <ul className="space-y-2" data-testid="requests-list">
            {requests.map((r) => (
              <RequestCard key={r.id} r={r} matLabel={matLabel}/>
            ))}
          </ul>
        )}
      </div>

      {showRequest && (
        <RequestMaterialModal project={project}
                              onClose={() => setShowRequest(false)}
                              onCreated={() => { setShowRequest(false); load(); }}/>
      )}
    </div>
  );
}

function RequestCard({ r, matLabel }) {
  const [showTimeline, setShowTimeline] = useState(false);
  const activity = Array.isArray(r.activity) ? r.activity : [];
  const hasActivity = activity.length > 0;
  const statusLabel = (s) => (s || "pending").replace("_", " ");

  return (
    <li className="border border-[#161616] rounded px-3 py-2"
        data-testid={`request-${r.id}`}>
      <div className="flex justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <p className="text-sm text-white">
            {matLabel(r.material_category)}
            {r.priority === "urgent" && (
              <span className="ml-2 text-[10px] font-mono uppercase tracking-widest text-red-400 border border-red-500/40 rounded px-1 py-0.5">
                Urgent
              </span>
            )}
          </p>
          <p className="text-[11px] text-gray-400 mt-1 whitespace-pre-wrap">{r.description}</p>
          <div className="mt-1 flex gap-1 flex-wrap">
            {r.send_to.map((t) => {
              const ts = r.team_status?.[t] || {};
              return (
                <span key={t}
                      className={`text-[10px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded border ${STATUS_TONE[ts.status || "pending"]}`}
                      title={ts.reason ? `Reason: ${ts.reason}` : ""}
                      data-testid={`request-${r.id}-team-${t}`}>
                  {TEAM_OPTIONS.find((o) => o.key === t)?.label || t}
                  <span className="text-gray-500 ml-1">· {statusLabel(ts.status)}</span>
                </span>
              );
            })}
          </div>
          {hasActivity && (
            <button onClick={() => setShowTimeline((v) => !v)}
                    className="mt-2 inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest text-gray-400 hover:text-[#d4af37] transition-colors"
                    data-testid={`request-${r.id}-toggle-timeline`}>
              {showTimeline ? <ChevronDown className="w-3 h-3"/> : <ChevronRight className="w-3 h-3"/>}
              <History className="w-3 h-3"/>
              Timeline ({activity.length})
            </button>
          )}
          {showTimeline && hasActivity && (
            <ul className="mt-2 border-l border-[#262626] pl-3 space-y-1.5"
                data-testid={`request-${r.id}-timeline`}>
              {activity.map((a, i) => (
                <li key={`${a.at}-${i}`} className="text-[10px] text-gray-400">
                  <span className="font-mono uppercase tracking-widest text-[#d4af37]">
                    {a.team ? (TEAM_OPTIONS.find((o) => o.key === a.team)?.label || a.team) : "Requester"}
                  </span>
                  <span className="text-gray-500"> · </span>
                  <span className={`font-mono uppercase tracking-widest ${
                    a.status === "rejected" ? "text-red-400" :
                    a.status === "dispatched" ? "text-emerald-300" :
                    a.status === "in_progress" ? "text-[#d4af37]" :
                    a.status === "received" ? "text-sky-300" : "text-gray-400"
                  }`}>{statusLabel(a.status)}</span>
                  <span className="text-gray-500"> by </span>
                  <span className="text-gray-300">{a.by_user_name || "—"}</span>
                  <span className="text-gray-600"> · {new Date(a.at).toLocaleString()}</span>
                  {a.reason && (
                    <span className="text-gray-500"> — “{a.reason}”</span>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
        <div className="text-right shrink-0">
          <span className={`text-[10px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded border ${STATUS_TONE[r.status || "pending"]}`}>
            {statusLabel(r.status)}
          </span>
          <p className="text-[10px] text-gray-500 mt-1">{new Date(r.created_at).toLocaleDateString()}</p>
        </div>
      </div>
    </li>
  );
}

function RequestMaterialModal({ project, onClose, onCreated }) {
  const [material, setMaterial] = useState("");
  const [description, setDescription] = useState("");
  const [quantity, setQuantity] = useState("");
  const [priority, setPriority] = useState("normal");
  const [teams, setTeams] = useState([]);
  const [notes, setNotes] = useState("");
  const [mediaAttachments, setMediaAttachments] = useState([]);
  const [busy, setBusy] = useState(false);
  const uploaded = mediaAttachments.map((a) => a.id).filter(Boolean);

  const toggleTeam = (k) => {
    setTeams((c) => c.includes(k) ? c.filter((x) => x !== k) : [...c, k]);
  };

  const submit = async () => {
    if (!material) { toast.error("Pick a material category"); return; }
    if (description.trim().length < 2) { toast.error("Description is required"); return; }
    if (teams.length === 0) { toast.error("Pick at least one team"); return; }
    setBusy(true);
    try {
      await api.post("/material-requests", {
        project_id: project.project_id || project.id,
        material_category: material,
        description: description.trim(),
        quantity: quantity.trim() || null,
        priority,
        send_to: teams,
        notes: notes.trim() || null,
        attachment_file_ids: uploaded,
      });
      toast.success("Material request sent");
      onCreated?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to raise request");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
         data-testid="request-material-modal">
      <div className="bg-[#0a0a0a] border border-[#262626] rounded-lg w-full max-w-2xl max-h-[92vh] flex flex-col">
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#262626]">
          <div>
            <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#d4af37]">Request Material</p>
            <h3 className="font-display text-xl text-white mt-0.5">{project.client_name} · {project.project_name}</h3>
          </div>
          <button onClick={onClose} className="text-gray-500 hover:text-white" data-testid="request-close">
            <X className="w-5 h-5"/>
          </button>
        </div>
        <div className="overflow-y-auto p-5 space-y-4">
          <div>
            <label className="block text-[10px] font-mono uppercase tracking-widest text-gray-400 mb-1">Material Category *</label>
            <select value={material} onChange={(e) => setMaterial(e.target.value)}
                    className="iz-input w-full text-xs" data-testid="req-material">
              <option value="">Select…</option>
              {MATERIAL_CATEGORIES.map((c) => (
                <option key={c.key} value={c.key}>{c.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-[10px] font-mono uppercase tracking-widest text-gray-400 mb-1">Description *</label>
            <textarea value={description} onChange={(e) => setDescription(e.target.value)}
                      rows={3} placeholder="What exactly is needed?"
                      className="iz-input w-full text-xs" data-testid="req-description"/>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-[10px] font-mono uppercase tracking-widest text-gray-400 mb-1">Quantity / Details</label>
              <input value={quantity} onChange={(e) => setQuantity(e.target.value)}
                     className="iz-input w-full text-xs" data-testid="req-quantity"/>
            </div>
            <div>
              <label className="block text-[10px] font-mono uppercase tracking-widest text-gray-400 mb-1">Priority</label>
              <div className="flex gap-2" data-testid="req-priority-group">
                {["normal", "urgent"].map((p) => (
                  <button key={p} onClick={() => setPriority(p)}
                          className={`flex-1 text-xs font-mono uppercase tracking-widest px-2 py-1 rounded border ${
                            priority === p
                              ? "border-[#d4af37] text-[#d4af37] bg-[#d4af37]/10"
                              : "border-[#262626] text-gray-500 hover:text-white"
                          }`}
                          data-testid={`req-priority-${p}`}>
                    {p}
                  </button>
                ))}
              </div>
            </div>
          </div>
          <div>
            <label className="block text-[10px] font-mono uppercase tracking-widest text-gray-400 mb-2">Send Request To *</label>
            <div className="grid grid-cols-2 gap-1.5" data-testid="req-teams-group">
              {TEAM_OPTIONS.map((t) => {
                const on = teams.includes(t.key);
                return (
                  <button key={t.key} onClick={() => toggleTeam(t.key)}
                          className={`text-xs px-3 py-1.5 rounded border text-left flex items-center gap-2 transition-colors ${
                            on
                              ? "border-[#d4af37] bg-[#d4af37]/10 text-[#d4af37]"
                              : "border-[#262626] text-gray-400 hover:text-white"
                          }`}
                          data-testid={`req-team-${t.key}`}>
                    <span className={`w-3 h-3 rounded-sm border ${on ? "bg-[#d4af37] border-[#d4af37]" : "border-[#333]"}`}/>
                    {t.label}
                  </button>
                );
              })}
            </div>
          </div>
          <div>
            <label className="block text-[10px] font-mono uppercase tracking-widest text-gray-400 mb-1">Notes · Voice · Photos</label>
            <MediaInputRow
              notes={notes}
              onNotesChange={setNotes}
              attachments={mediaAttachments}
              onAttachmentsChange={setMediaAttachments}
              placeholder="Add site notes (or tap mic for voice · Kn/Hi/Te/En)"
              testidPrefix="req-media"/>
          </div>
        </div>
        <div className="border-t border-[#262626] px-5 py-3 flex justify-end gap-2">
          <button onClick={onClose} className="iz-btn-secondary text-xs">Cancel</button>
          <button onClick={submit} disabled={busy}
                  className="iz-btn-primary text-xs flex items-center gap-1.5"
                  data-testid="req-submit">
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin"/> : <Send className="w-3.5 h-3.5"/>}
            Send Request
          </button>
        </div>
      </div>
    </div>
  );
}
